import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export type ExpensesListPageLayoutProps = {
  children: React.ReactNode
  /**
   * Whether to use the DataCollection-optimized layout.
   * - true: Uses custom Box layout for proper SyncFooter flexbox positioning
   * - false: Uses ResponsivePage.Body for DataTable infinite scroll compatibility
   */
  useDataCollectionLayout?: boolean
}

/**
 * Layout wrapper for ExpensesListPage that handles two different layout modes.
 *
 * ## DataCollection mode (useDataCollectionLayout=true)
 *
 * The `SyncFooter` component needs to be positioned at the bottom of the viewport,
 * not at the end of scrollable content. This requires a flex column layout with
 * proper height constraints.
 *
 * However, `ResponsivePage.Body`'s internal `Container` component has `overflow: auto`
 * without a height constraint, which breaks the CSS height chain. When wrapped by
 * `ResponsivePage.Body`, our `height: 100%` becomes 100% of "auto" (content height),
 * making the flexbox layout ineffective.
 *
 * We render `ResponsivePage.Body` hidden (to maintain framework integration like
 * error boundaries), then render our layout Box as a sibling. This bypasses the
 * Container's height issues while preserving framework functionality.
 *
 * ## DataTable mode (useDataCollectionLayout=false)
 *
 * The deprecated DataTable component relies on ResponsivePage.Body's scroll container
 * for infinite scroll to work correctly. We preserve the original behavior by rendering
 * ResponsivePage.Body variant="full".
 *
 * TODO: Simplify it when we migrate to DataCollection. Reconsider fixing the SyncFooter - ResponsivePage.Body issue.
 */
export const ExpensesListPageLayout = ({
  children,
  useDataCollectionLayout = false,
}: ExpensesListPageLayoutProps) => {
  // DataTable mode: use ResponsivePage.Body for proper infinite scroll support
  if (!useDataCollectionLayout) {
    return <ResponsivePage.Body variant="full">{children}</ResponsivePage.Body>
  }

  // DataCollection mode: render ResponsivePage.Body hidden (for framework integration)
  // and use a sibling Box for proper SyncFooter flexbox positioning
  return (
    <>
      <Box className="hidden">
        <ResponsivePage.Body variant="fullscreen">&nbsp;</ResponsivePage.Body>
      </Box>
      <Box height="full">{children}</Box>
    </>
  )
}
